
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Function to ensure required storage buckets exist
export const ensureStorageBuckets = async (): Promise<boolean> => {
  try {
    console.log("Checking if storage buckets exist...");
    // Check existing buckets
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error('Error listing storage buckets:', error);
      return false;
    }
    
    // Check if 'videos' bucket exists
    const videosBucketExists = buckets?.some(b => b.name === 'videos');
    if (!videosBucketExists) {
      console.log('Creating videos bucket');
      const { error: createError } = await supabase.storage.createBucket('videos', {
        public: true,
        fileSizeLimit: 50000000 // 50MB limit to avoid size issues
      });
      
      if (createError) {
        console.error('Error creating videos bucket:', createError);
        return false;
      }
      
      // Skip policy creation via RPC as it's causing issues
      // Instead, let's rely on the 'public: true' setting in bucket creation
      console.log('Videos bucket created successfully with public access');
      toast.success('Videos storage initialized');
    } else {
      console.log('Videos bucket exists');
    }
    
    // Check if 'avatars' bucket exists
    const avatarsBucketExists = buckets?.some(b => b.name === 'avatars');
    if (!avatarsBucketExists) {
      console.log('Creating avatars bucket');
      const { error: createAvatarsError } = await supabase.storage.createBucket('avatars', {
        public: true,
        fileSizeLimit: 50000000 // 50MB limit
      });
      
      if (createAvatarsError) {
        console.error('Error creating avatars bucket:', createAvatarsError);
      }
    }
    
    // Check if 'logos' bucket exists
    const logosBucketExists = buckets?.some(b => b.name === 'logos');
    if (!logosBucketExists) {
      console.log('Creating logos bucket');
      const { error: createLogosError } = await supabase.storage.createBucket('logos', {
        public: true,
        fileSizeLimit: 10000000 // 10MB limit for logos
      });
      
      if (createLogosError) {
        console.error('Error creating logos bucket:', createLogosError);
      } else {
        console.log('Logos bucket created successfully with public access');
      }
    }
    
    return true;
  } catch (error) {
    console.error('Exception in ensureStorageBuckets:', error);
    return false;
  }
};

// Call this function when the app starts
export const initializeStorage = async (): Promise<void> => {
  console.log("Initializing storage buckets...");
  const success = await ensureStorageBuckets();
  console.log(`Storage initialization ${success ? 'successful' : 'failed'}`);
};

// Function to get a proper public URL for a file path
export const getPublicUrl = (bucket: string, path: string): string => {
  if (!path) return '';
  
  // If it's already a URL, return it
  if (path.startsWith('http')) return path;
  
  // Get the public URL from Supabase
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl || '';
};
